import {
  Button,
  Flex,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import axios from 'axios';
import { Else, If, Then } from 'react-if';

import GreenCheckmark from 'assets/icons/green-checkmark.svg';
import RedExclamation from 'assets/icons/red-exclamation.svg';
import { AsyncWrapper } from 'components/AsyncWrapper';
import AsyncButton from 'components/form/AsyncButton';
import SubscriptionPlansModal from 'components/SubscriptionPlansModal';
import useAxios from 'hooks/useAxios';
import useUserContext from 'hooks/useUserContext';

export default function Billing(): JSX.Element {
  const { user } = useUserContext();

  const subscription = user?.clinic?.subscription;
  const hasSubscription = Boolean(subscription);

  const isSubscriptionManagedByTenant = subscription?.managedByTenant ?? false;

  const [paymentMethodRequest] = useAxios<boolean>('/api/billing/pay-per-use/check-payment-method');
  const isPaymentMethod = paymentMethodRequest.data;

  const {
    isOpen: isSubscribeModalOpen,
    onOpen: onSubscribeModalOpen,
    onClose: onSubscribeModalClose,
  } = useDisclosure();

  async function handleRedirectToCustomerPortal(): Promise<void> {
    const { data } = await axios.post<{ url: string }>('/api/billing/stripe-customer-portal');
    window.open(data.url, '_blank');
  }

  async function handleSignUpClick(): Promise<void> {
    const { data } = await axios.post<{ url: string }>('/api/billing/pay-per-use/setup-checkout-session');
    window.location.href = data.url;
  }

  return (
    <AsyncWrapper requests={[paymentMethodRequest]}>
      <SubscriptionPlansModal
        isOpen={isSubscribeModalOpen}
        onClose={onSubscribeModalClose}
      />

      <Flex flexDirection='column' mt='25px' gap='10px'>
        <Text fontWeight='bold' fontSize='18px'>
          Skyline Subscription
        </Text>
        <Flex
          mb='50px'
          width='894px'
          height='100px'
          justifyContent='space-between'
          alignItems='center'
          border='1px solid #718096'
          borderRadius='12px'
          padding='20px'
        >
          <If condition={hasSubscription}>
            <Then>
              <If condition={isSubscriptionManagedByTenant}>
                <Then>
                  <Flex gap='7px' alignItems='center'>
                    <img src={GreenCheckmark} alt='Active subscription' />
                    <Text>Subscription is managed by your dealer</Text>
                  </Flex>
                </Then>
                <Else>
                  <Flex gap='7px' alignItems='center' justifyContent='space-between' width='100%'>
                    <Flex gap='7px' alignItems='center'>
                      <img src={GreenCheckmark} alt='Active subscription' />
                      <Text>Manage my subscription</Text>
                    </Flex>
                    <AsyncButton onClick={handleRedirectToCustomerPortal} width='200px'>
                      Manage subscription
                    </AsyncButton>
                  </Flex>
                </Else>
              </If>
            </Then>
            <Else>
              <Flex gap='7px' alignItems='center' justifyContent='space-between' width='100%'>
                <Flex gap='7px' alignItems='center'>
                  <img src={RedExclamation} alt='No active subscription' />
                  <Text>You currently do not have an active Skyline Subscription.</Text>
                </Flex>
                <Button onClick={onSubscribeModalOpen} width='200px'>
                  Subscribe
                </Button>
              </Flex>
            </Else>
          </If>
        </Flex>

        <Text fontWeight='bold' fontSize='18px' mb='10px'>
          Usage Based Billing
        </Text>
        <Flex
          width='894px'
          height='100px'
          justifyContent='space-between'
          alignItems='center'
          border='1px solid #718096'
          borderRadius='12px'
          padding='20px'
        >
          <If condition={isPaymentMethod}>
            <Then>
              <Flex gap='7px' alignItems='center' justifyContent='space-between' width='100%'>
                <Flex gap='7px'>
                  <img src={GreenCheckmark} alt='Payment method exists' />
                  <Text>Billing info is up to date</Text>
                </Flex>
                <AsyncButton onClick={handleRedirectToCustomerPortal}>
                  Manage billing
                </AsyncButton>
              </Flex>
            </Then>
            <Else>
              <Flex gap='7px' alignItems='center' justifyContent='space-between' width='100%'>
                <Flex gap='7px'>
                  <img src={RedExclamation} alt='Not setup' />
                  <Text>You are not setup for usage based billing</Text>
                </Flex>
                <AsyncButton width='200px' onClick={handleSignUpClick}>
                  Start set up
                </AsyncButton>
              </Flex>
            </Else>
          </If>
        </Flex>
      </Flex>
    </AsyncWrapper>
  );
}
